import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  Eye,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Briefcase,
  MapPin,
  Phone,
  X,
  Mail,
  Tag as TagIcon,
  Plus,
  FileText,
  User,
  Globe,
  Search,
  Filter,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  GetAxios,
  PostAxios,
  DeleteAxios,
  GetBlobAxios,
} from "../../../helpers/axiosHelpers";
import { toast } from "sonner";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SubmissionsTable = ({
  submissions: initialSubmissions = [],
  pagination: initialPagination,
  onPageChange,
  onDelete,
  refreshData,
}) => {
  const [selectedApp, setSelectedApp] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // PDF viewer states
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    tagId: "",
    startDate: "",
    endDate: "",
  });

  // Local data state for filtering
  const [submissions, setSubmissions] = useState(initialSubmissions);
  const [pagination, setPagination] = useState(initialPagination);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    setSubmissions(initialSubmissions);
    setPagination(initialPagination);
  }, [initialSubmissions, initialPagination]);

  const fetchTags = async () => {
    try {
      const response = await GetAxios("/tags");
      if (response.success) {
        setAvailableTags(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch tags:", error);
    }
  };

  const fetchFilteredData = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });

      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.tagId) params.append("tagId", filters.tagId);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);

      const response = await GetAxios(`/application?${params.toString()}`);
      if (response.success) {
        setSubmissions(response.data.applications);
        setPagination(response.data.meta);
      }
    } catch (error) {
      toast.error("Failed to fetch applications");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchFilteredData(1);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      tagId: "",
      startDate: "",
      endDate: "",
    });
    if (refreshData) {
      refreshData(1);
    } else {
      fetchFilteredData(1);
    }
  };

  const handleViewResume = async (applicationId, resumeUrl) => {
    const isPdf = resumeUrl.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      try {
        // Fetch the PDF with authentication via cookies
        const blob = await GetBlobAxios(
          `/application/${applicationId}/cv/view`,
        );

        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
        setShowPdfViewer(true);
      } catch (error) {
        toast.error("Failed to load resume");
        console.error("Error loading resume:", error);
      }
    } else {
      // For non-PDF files, trigger download
      try {
        const blob = await GetBlobAxios(`/application/${applicationId}/cv`);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = resumeUrl.split("/").pop(); // Get filename from path
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        toast.error("Failed to download file");
        console.error("Error downloading file:", error);
      }
    }
  };

  const handleTagToggle = async (applicationId, tagId, isCurrentlyTagged) => {
    try {
      let response;
      if (isCurrentlyTagged) {
        response = await DeleteAxios(
          `/application/${applicationId}/tags/${tagId}`,
        );
      } else {
        response = await PostAxios(
          `/application/${applicationId}/tags/${tagId}`,
          {},
        );
      }

      if (response.success) {
        toast.success(isCurrentlyTagged ? "Tag removed" : "Tag added");
        // Refresh the selected app data
        if (selectedApp && selectedApp.id === applicationId) {
          const updatedApp = { ...selectedApp };
          if (isCurrentlyTagged) {
            updatedApp.tags = updatedApp.tags.filter((t) => t.tag.id !== tagId);
          } else {
            const tag = availableTags.find((t) => t.id === tagId);
            updatedApp.tags = [
              ...(updatedApp.tags || []),
              { tag, assignedAt: new Date() },
            ];
          }
          setSelectedApp(updatedApp);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.msg || "Failed to update tag");
    }
  };

  const getStatusStyle = (status) => {
    switch (status?.toUpperCase()) {
      case "NEW":
        return "bg-blue-50 text-blue-600 border-blue-100";
      case "REVIEWED":
        return "bg-emerald-50 text-emerald-600 border-emerald-100";
      default:
        return "bg-slate-50 text-slate-600 border-slate-100";
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Filters Section */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black text-slate-700 flex items-center">
            <Filter size={16} className="mr-2" />
            Advanced Filters
          </h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-xs font-bold text-subidha-blue hover:text-blue-700 transition-colors"
          >
            {showFilters ? "Hide" : "Show"} Filters
          </button>
        </div>

        {showFilters && (
          <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
            {/* Search Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  Search (Name/Email)
                </label>
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    placeholder="Type to search..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue"
                >
                  <option value="">All Statuses</option>
                  <option value="NEW">New</option>
                  <option value="IN_REVIEW">In Review</option>
                  <option value="SHORTLISTED">Shortlisted</option>
                  <option value="INTERVIEWED">Interviewed</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  Tag
                </label>
                <select
                  value={filters.tagId}
                  onChange={(e) => handleFilterChange("tagId", e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue"
                >
                  <option value="">All Tags</option>
                  {availableTags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Range Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-6 py-2 bg-subidha-blue text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50"
              >
                {loading ? "Loading..." : "Apply Filters"}
              </button>
              <button
                onClick={clearFilters}
                disabled={loading}
                className="px-6 py-2 bg-slate-100 text-slate-700 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-200 transition-all disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 1. Table Content */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="px-8 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Candidate
              </th>
              <th className="px-8 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Details
              </th>
              <th className="px-8 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Status
              </th>
              <th className="px-8 py-5 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {submissions.length > 0 ? (
              submissions.map((sub) => (
                <tr
                  key={sub.id}
                  className="hover:bg-slate-50/30 transition-colors group"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center font-black text-white text-[10px]">
                        {sub.firstName?.[0]}
                        {sub.lastName?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900">
                          {sub.firstName} {sub.lastName}
                        </p>
                        <p className="text-[11px] font-bold text-slate-400">
                          {sub.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-bold text-slate-700 flex items-center">
                        <Briefcase
                          size={12}
                          className="mr-1.5 text-slate-400"
                        />{" "}
                        {sub.visaType}
                      </span>
                      <span className="text-[10px] font-black text-subidha-blue uppercase">
                        JLPT {sub.japaneseLevel}
                      </span>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span
                      className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase border ${getStatusStyle(sub.status)}`}
                    >
                      {sub.status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setSelectedApp(sub)}
                        className="p-2 text-slate-600 hover:text-subidha-blue hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const blob = await GetBlobAxios(
                              `/application/${sub.id}/cv`,
                            );
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `CV-${sub.firstName}-${sub.lastName}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error("Download failed:", err);
                            toast.error("Failed to download resume");
                          }
                        }}
                        className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => onDelete(sub.id)}
                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="4"
                  className="py-20 text-center text-slate-300 font-black uppercase tracking-widest text-xs"
                >
                  No applications found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 2. Pagination Footer */}
      {pagination && (
        <div className="px-8 py-6 border-t border-slate-100 bg-white flex items-center justify-between">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Showing <span className="text-slate-900">{submissions.length}</span>{" "}
            of <span className="text-slate-900">{pagination.total}</span> Global
            Entries
          </p>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const newPage = pagination.page - 1;
                if (Object.values(filters).some((f) => f)) {
                  fetchFilteredData(newPage);
                } else if (onPageChange) {
                  onPageChange(newPage);
                }
              }}
              disabled={pagination.page <= 1}
              className="p-2 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 disabled:opacity-20 transition-all"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
              <span className="text-xs font-black text-slate-900">
                {pagination.page} <span className="text-slate-300 mx-1">/</span>{" "}
                {pagination.totalPages}
              </span>
            </div>

            <button
              onClick={() => {
                const newPage = pagination.page + 1;
                if (Object.values(filters).some((f) => f)) {
                  fetchFilteredData(newPage);
                } else if (onPageChange) {
                  onPageChange(newPage);
                }
              }}
              disabled={pagination.page >= pagination.totalPages}
              className="p-2 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 disabled:opacity-20 transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* 3. Candidate Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-8 bg-slate-900 text-white flex justify-between items-start">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-2xl font-black">
                  {selectedApp.firstName?.[0]}
                  {selectedApp.lastName?.[0]}
                </div>
                <div>
                  <h2 className="text-2xl font-black tracking-tight">
                    {selectedApp.firstName} {selectedApp.lastName}
                  </h2>
                  <p className="text-blue-400 text-xs font-black uppercase tracking-widest">
                    {selectedApp.visaType} Applicant
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedApp(null)}
                className="p-2 hover:bg-white/10 rounded-xl transition-all"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-8 space-y-6 bg-white max-h-[60vh] overflow-y-auto">
              {/* Personal Information */}
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoBlock
                    label="Email Address"
                    value={selectedApp.email}
                    icon={Mail}
                  />
                  <InfoBlock
                    label="Phone Number"
                    value={selectedApp.phone}
                    icon={Phone}
                  />
                  <InfoBlock
                    label="Nationality"
                    value={selectedApp.nationality}
                    icon={Globe}
                  />
                  <InfoBlock
                    label="Date of Birth"
                    value={new Date(selectedApp.dob).toLocaleDateString()}
                    icon={Calendar}
                  />
                  <InfoBlock
                    label="Gender"
                    value={selectedApp.gender}
                    icon={User}
                  />
                  <InfoBlock
                    label="Status"
                    value={
                      <span
                        className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase border ${getStatusStyle(selectedApp.status)}`}
                      >
                        {selectedApp.status}
                      </span>
                    }
                    icon={Briefcase}
                  />
                </div>
              </div>

              {/* Visa Information */}
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">
                  Visa Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoBlock
                    label="Visa Type"
                    value={selectedApp.visaType}
                    icon={FileText}
                  />
                  <InfoBlock
                    label="Visa Expiry"
                    value={new Date(
                      selectedApp.visaExpiry,
                    ).toLocaleDateString()}
                    icon={Calendar}
                  />
                  <InfoBlock
                    label="Japanese Level"
                    value={selectedApp.japaneseLevel}
                    icon={Briefcase}
                  />
                </div>
              </div>

              {/* Resume */}
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">
                  Resume
                </h3>
                <button
                  onClick={() =>
                    handleViewResume(selectedApp.id, selectedApp.resumeUrl)
                  }
                  className="flex items-center space-x-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all"
                >
                  <FileText size={16} className="text-slate-600" />
                  <span className="text-sm font-bold text-slate-700">
                    View Resume
                  </span>
                </button>
              </div>

              {/* Tags */}
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center justify-between">
                  <span>Tags</span>
                  <button
                    onClick={() => setShowTagModal(true)}
                    className="text-subidha-blue hover:text-blue-700 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedApp.tags && selectedApp.tags.length > 0 ? (
                    selectedApp.tags.map((appTag) => (
                      <div
                        key={appTag.tag.id}
                        className="flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-bold border"
                        style={{
                          backgroundColor: `${appTag.tag.color}20`,
                          borderColor: appTag.tag.color,
                          color: appTag.tag.color,
                        }}
                      >
                        <TagIcon size={12} />
                        <span>{appTag.tag.name}</span>
                        <button
                          onClick={() =>
                            handleTagToggle(selectedApp.id, appTag.tag.id, true)
                          }
                          className="hover:opacity-70 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-400">No tags assigned</p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-8 bg-slate-50 border-t border-slate-100 flex gap-4">
              <button className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-subidha-blue transition-all">
                Update Status
              </button>
              <button
                onClick={() => setSelectedApp(null)}
                className="px-8 py-4 bg-white border border-slate-200 text-slate-500 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Selection Modal */}
      {showTagModal && selectedApp && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="text-lg font-black">Add Tag</h3>
              <button
                onClick={() => setShowTagModal(false)}
                className="p-2 hover:bg-white/10 rounded-xl transition-all"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                {availableTags
                  .filter(
                    (tag) =>
                      !selectedApp.tags?.some(
                        (appTag) => appTag.tag.id === tag.id,
                      ),
                  )
                  .map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => {
                        handleTagToggle(selectedApp.id, tag.id, false);
                        setShowTagModal(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl border hover:bg-slate-50 transition-all"
                      style={{ borderColor: tag.color }}
                    >
                      <TagIcon size={16} style={{ color: tag.color }} />
                      <span className="text-sm font-bold text-slate-700">
                        {tag.name}
                      </span>
                    </button>
                  ))}
                {availableTags.filter(
                  (tag) =>
                    !selectedApp.tags?.some(
                      (appTag) => appTag.tag.id === tag.id,
                    ),
                ).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    All tags have been assigned
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfViewer && pdfUrl && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[2rem] w-full max-w-6xl h-[90vh] shadow-2xl overflow-hidden animate-in zoom-in-95 flex flex-col">
            {/* Header */}
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="text-lg font-black">Resume Viewer</h3>
              <div className="flex items-center space-x-2">
                <a
                  href={pdfUrl}
                  download
                  className="p-2 hover:bg-white/10 rounded-xl transition-all"
                >
                  <Download size={20} />
                </a>
                <button
                  onClick={() => {
                    setShowPdfViewer(false);
                    // Clean up blob URL to prevent memory leaks
                    if (pdfUrl && pdfUrl.startsWith("blob:")) {
                      URL.revokeObjectURL(pdfUrl);
                    }
                    setPdfUrl(null);
                    setPageNumber(1);
                    setScale(1.0);
                  }}
                  className="p-2 hover:bg-white/10 rounded-xl transition-all"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* PDF Controls */}
            <div className="px-6 py-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                  disabled={pageNumber <= 1}
                  className="p-2 rounded-lg border border-slate-300 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-bold text-slate-700 min-w-[100px] text-center">
                  Page {pageNumber} of {numPages || "..."}
                </span>
                <button
                  onClick={() =>
                    setPageNumber((prev) => Math.min(prev + 1, numPages))
                  }
                  disabled={pageNumber >= numPages}
                  className="p-2 rounded-lg border border-slate-300 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setScale((prev) => Math.max(prev - 0.2, 0.5))}
                  disabled={scale <= 0.5}
                  className="p-2 rounded-lg border border-slate-300 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ZoomOut size={16} />
                </button>
                <span className="text-sm font-bold text-slate-700 min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale((prev) => Math.min(prev + 0.2, 2.0))}
                  disabled={scale >= 2.0}
                  className="p-2 rounded-lg border border-slate-300 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ZoomIn size={16} />
                </button>
              </div>
            </div>

            {/* PDF Content */}
            <div className="flex-1 overflow-auto bg-slate-200 p-4 flex justify-center">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(error) => {
                  console.error("Error loading PDF:", error);
                  toast.error("Failed to load PDF");
                }}
                loading={
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-subidha-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-sm font-bold text-slate-600">
                        Loading PDF...
                      </p>
                    </div>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  className="shadow-lg"
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Helper Component for Modal Content */
const InfoBlock = ({ label, value, icon: Icon }) => (
  <div className="space-y-1">
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
      {label}
    </p>
    <div className="flex items-center space-x-2 text-slate-900 font-bold">
      <Icon size={14} className="text-slate-400" />
      {typeof value === "string" ? (
        <span className="text-sm">{value || "Not provided"}</span>
      ) : (
        value
      )}
    </div>
  </div>
);

export default SubmissionsTable;
